/**
 * Frontend Gift Registry API Service
 */

import { apiClient } from './client';
import type {
  CelebrationRegistry,
  CreateRegistryRequest,
  UpdateRegistryRequest,
  ReorderRegistriesRequest,
  RegistryPlatformInfo,
  ApiResponse
} from '@/types/celebrationRegistry.types';

export class CelebrationRegistryService {
  /**
   * Get all registries for a celebration
   */
  async getRegistries(celebrationId: number): Promise<CelebrationRegistry[]> {
    try {
      const response = await apiClient.get<ApiResponse<CelebrationRegistry[]>>(
        `/celebrations/${celebrationId}/registries`
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching registries:', error);
      throw error;
    }
  }

  /**
   * Create a new registry
   */
  async createRegistry(
    celebrationId: number,
    data: CreateRegistryRequest
  ): Promise<CelebrationRegistry> {
    try {
      const response = await apiClient.post<ApiResponse<CelebrationRegistry>>(
        `/celebrations/${celebrationId}/registries`,
        data
      );
      return response.data.data!;
    } catch (error) {
      console.error('Error creating registry:', error);
      throw error;
    }
  }

  /**
   * Update a registry
   */
  async updateRegistry(
    celebrationId: number,
    registryId: number,
    data: UpdateRegistryRequest
  ): Promise<CelebrationRegistry> {
    try {
      const response = await apiClient.put<ApiResponse<CelebrationRegistry>>(
        `/celebrations/${celebrationId}/registries/${registryId}`,
        data
      );
      return response.data.data!;
    } catch (error) {
      console.error('Error updating registry:', error);
      throw error;
    }
  }

  /**
   * Delete a registry
   */
  async deleteRegistry(celebrationId: number, registryId: number): Promise<void> {
    try {
      await apiClient.delete(`/celebrations/${celebrationId}/registries/${registryId}`);
    } catch (error) {
      console.error('Error deleting registry:', error);
      throw error;
    }
  }

  /**
   * Reorder registries
   */
  async reorderRegistries(
    celebrationId: number,
    data: ReorderRegistriesRequest
  ): Promise<void> {
    try {
      await apiClient.put(
        `/celebrations/${celebrationId}/registries/reorder`,
        data
      );
    } catch (error) {
      console.error('Error reordering registries:', error);
      throw error;
    }
  }

  /**
   * Track a registry click for analytics
   */
  async trackRegistryClick(celebrationId: number, registryId: number): Promise<void> {
    try {
      await apiClient.post(
        `/celebrations/${celebrationId}/registries/${registryId}/click`
      );
    } catch (error) {
      console.error('Error tracking registry click:', error);
      // Don't throw - analytics tracking failure shouldn't affect user experience
    }
  }

  /**
   * Get list of popular registry platforms
   */
  async getPlatforms(): Promise<RegistryPlatformInfo[]> {
    try {
      const response = await apiClient.get<ApiResponse<RegistryPlatformInfo[]>>(
        '/registries/platforms'
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching registry platforms:', error);
      throw error;
    }
  }
}

export const celebrationRegistryService = new CelebrationRegistryService();
